import { ApiMethods, ApiTags, appAPI } from '.';

// Roles are LLM-canonical: user / assistant / system. The kind of
// content a message carries is encoded by its parts (see MessagePart).
// An assistant message can interleave text and tool_use parts the way
// Anthropic's Messages API does; a system message carries deploy events
// or status text; a user message is the original prompt or future
// tool_result data.
export type BuilderMessageRole = 'user' | 'assistant' | 'system';

// Part type discriminator. Renderers branch on this. New content kinds
// ship as new part types — `role` does not grow.
export type MessagePartType =
  | 'text'
  | 'tool_use'
  | 'tool_result'
  | 'task_list'
  | 'deploy'
  | 'error';

// MessagePart mirrors backend builder_message.MessagePart. Only the
// fields documented for that part type are populated; the rest are
// undefined.
export interface MessagePart {
  type: string;
  // type=text
  text?: string;
  // type=tool_use
  toolUseId?: string;
  toolName?: string;
  // Backend sends ToolInput as `json.RawMessage`, which serializes as
  // inline JSON (object/array/primitive) rather than a JSON-encoded
  // string. Typed as `unknown` so renderers must narrow before use.
  toolInput?: unknown;
  // type=tool_result
  toolResultFor?: string;
  toolResult?: string;
  toolIsError?: boolean;
  // type=task_list — `tasks` carries the inline task array (backend
  // serializes it from `json.RawMessage`, not as a JSON-encoded string);
  // `currentIndex` points at the task currently in flight.
  tasks?: unknown[];
  currentIndex?: number;
  // type=deploy
  deployStatus?: string;
  deployUrl?: string;
  deployId?: string;
  deployError?: string;
  // type=error
  errorMessage?: string;
}

export interface BuilderMessage {
  id: string;
  builderSessionId: string;
  role: string;
  parts: MessagePart[];
  turnId?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Extract the concatenated text from a message's parts. */
export const getMessageText = (msg: BuilderMessage): string =>
  (msg.parts ?? [])
    .filter((p) => p.type === 'text')
    .map((p) => p.text ?? '')
    .join('\n\n');

export interface BuilderSession {
  id: string;
  appBuilderId: string;
  claudeSessionId?: string;
  summary?: string;
  mode?: 'code' | 'plan' | string;
  // Non-empty while a turn is in flight; cleared when the turn completes.
  // Drives the chat thinking indicator and composer lock.
  activeTurnId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ListBuilderMessagesResponse {
  data: BuilderMessage[];
  nextToken?: string;
}

export const getBuilderSessionEntityTag = (id: string) => ({
  type: ApiTags.builderSessions as const,
  id,
});

export const getBuilderSessionMessagesTag = (builderSessionId: string) => ({
  type: ApiTags.builderMessages as const,
  id: `MESSAGES-${builderSessionId}`,
});

/**
 * Merge an incoming WS message event into an existing transcript list.
 *
 * The same message ID can arrive twice (once from the GET hydrate, once from
 * a near-simultaneous WS event), and MODIFY events arrive throughout streaming
 * as content is appended. Dedupe by ID and merge fields so the latest payload
 * wins on overlap.
 *
 * Pure function — exported so reducers/tests can call it without RTK plumbing.
 */
export const mergeBuilderMessages = (
  existing: BuilderMessage[],
  incoming: BuilderMessage[],
): BuilderMessage[] => {
  if (incoming.length === 0) {
    return existing;
  }

  const merged = [...existing];
  const indexById = new Map<string, number>();
  merged.forEach((message, index) => {
    indexById.set(message.id, index);
  });

  for (const message of incoming) {
    if (!message.id) continue;
    const existingIndex = indexById.get(message.id);
    if (existingIndex === undefined) {
      indexById.set(message.id, merged.length);
      merged.push(message);
    } else {
      merged[existingIndex] = { ...merged[existingIndex], ...message };
    }
  }

  return merged;
};

export interface ListBuilderSessionsResponse {
  data: BuilderSession[];
  nextToken?: string;
}

export const builderSessionApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    listBuilderSessions: build.query<
      ListBuilderSessionsResponse,
      { appBuilderId: string }
    >({
      query: ({ appBuilderId }) => ({
        path: `/app-builders/${appBuilderId}/sessions`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (_, __, arg) => [
        { type: ApiTags.builderSessions, id: `LIST-${arg.appBuilderId}` },
      ],
    }),
    getBuilderSession: build.query<
      BuilderSession,
      { appBuilderId: string; sessionId: string }
    >({
      query: ({ appBuilderId, sessionId }) => ({
        path: `/app-builders/${appBuilderId}/sessions/${sessionId}`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (_, __, arg) => [getBuilderSessionEntityTag(arg.sessionId)],
    }),
    listBuilderSessionMessages: build.query<
      ListBuilderMessagesResponse,
      {
        appBuilderId: string;
        builderSessionId: string;
        limit?: number;
        nextToken?: string;
      }
    >({
      query: ({ appBuilderId, builderSessionId, limit, nextToken }) => {
        const qs = new URLSearchParams();
        if (limit != null) qs.set('limit', String(limit));
        if (nextToken) qs.set('nextToken', nextToken);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        return {
          path: `/app-builders/${appBuilderId}/sessions/${builderSessionId}/messages${suffix}`,
          method: ApiMethods.get,
          options: {},
        };
      },
      providesTags: (_, __, arg) => [
        getBuilderSessionMessagesTag(arg.builderSessionId),
      ],
    }),
    sendBuilderSessionMessage: build.mutation<
      { data: { turnId: string; messageId: string } },
      { appBuilderId: string; builderSessionId: string; content: string }
    >({
      query: ({ appBuilderId, builderSessionId, content }) => ({
        path: `/app-builders/${appBuilderId}/sessions/${builderSessionId}/messages`,
        method: ApiMethods.post,
        options: { body: { content } },
      }),
      invalidatesTags: (_, __, arg) => [
        { type: ApiTags.builderSessions, id: `LIST-${arg.appBuilderId}` },
        getBuilderSessionMessagesTag(arg.builderSessionId),
      ],
    }),
  }),
});

export const {
  useListBuilderSessionsQuery,
  useGetBuilderSessionQuery,
  useListBuilderSessionMessagesQuery,
  useSendBuilderSessionMessageMutation,
} = builderSessionApi;
